<template>
  <v-container fluid>
    <v-card>
      <v-toolbar dense style="background:#F5F5F5">
        <v-toolbar-title dense style="color:#607D8B;font-weight:bold">Administration des Jours Ouvrés</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn @click="refresh" icon>
          <v-icon color="#009688" large>mdi-refresh</v-icon>
          <template v-slot:loader>
            <span class="custom-loader"><v-icon light>cached</v-icon></span>
          </template>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-dialog fullscreen v-model="loading">
          <v-container fill-height fluid style="background-color: rgba(200, 200, 200, 0.5);">
            <v-layout align-center="true" justify-center="true">
              <v-progress-circular
                :on-cancel="onCancel"
                :size="70"
                color="#009688"
                indeterminate>
              </v-progress-circular>
            </v-layout>
          </v-container>
        </v-dialog>

        <v-alert :color="AlertColor(type)"
                 :dismissible="true"
                 :value="true"
                 @input="dismissible_close"
                 border="top"
                 class="pre-formatted multi-line"
                 colored-border
                 elevation="2"
                 icon="check_circle"
                 style="color: #546E7A"
                 v-if="type">
          {{ text }}
        </v-alert>

        <!-- Sélecteur d'année et actions -->
        <v-card class="mt-1" flat tile>
          <v-row>
            <v-col cols="12" md="3">
              <v-select
                :items="availableYears"
                v-model="selectedYear"
                label="Sélectionner l'année"
                @change="loadYearData"
                outlined
                dense
                :disabled="loading">
              </v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-btn
                :disabled="loading || !selectedYear"
                @click="calculerAnneeComplete"
                block
                class="custom-btn white--text"
                color="#FF9800"
                height="45px">
                <v-icon class="mr-1">mdi-calculator</v-icon>
                <span>Calcul automatique {{ selectedYear }}</span>
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
          </v-row>
        </v-card>

        <!-- Tableau des mois -->
        <v-card class="mt-4" elevation="6">
          <v-card-title class="text-center">
            <h3>Jours ouvrés pour l'année {{ selectedYear }}</h3>
            <v-spacer></v-spacer>
            <v-chip small color="green" text-color="white">
              <v-icon small left>mdi-check-circle</v-icon>
              Calculé
            </v-chip>
            <v-chip small color="orange" text-color="white" class="ml-2">
              <v-icon small left>mdi-alert-circle</v-icon>
              Manquant
            </v-chip>
            <v-chip small color="blue" text-color="white" class="ml-2">
              <v-icon small left>mdi-loading</v-icon>
              En cours
            </v-chip>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="monthsData"
            :loading="loading"
            class="elevation-1"
            hide-default-footer
            disable-pagination>
            
            <template v-slot:[`item.mois`]="{ item }">
              <span :class="{ 'font-weight-bold primary--text': isCurrentMonth(item.numeroMois) }">
                {{ item.mois }}
                <v-chip x-small color="primary" v-if="isCurrentMonth(item.numeroMois)">ACTUEL</v-chip>
              </span>
            </template>

            <template v-slot:[`item.nbrJoursOuvres`]="{ item }">
              <v-chip
                :color="getStatusColor(item.nbrJoursOuvres)"
                :text-color="getStatusTextColor(item.nbrJoursOuvres)"
                small>
                {{ item.nbrJoursOuvres || 'Non défini' }}
              </v-chip>
            </template>

            <template v-slot:[`item.statut`]="{ item }">
              <v-icon
                :color="getStatusIconColor(item.nbrJoursOuvres)"
                size="20">
                {{ getStatusIcon(item.nbrJoursOuvres) }}
              </v-icon>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="openEditModal(item)"
                    icon
                    small
                    color="primary"
                    v-on="on">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Modifier</span>
              </v-tooltip>
              
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="calculerMois(item)"
                    icon
                    small
                    color="orange"
                    v-on="on"
                    :disabled="loading">
                    <v-icon>mdi-calculator</v-icon>
                  </v-btn>
                </template>
                <span>Calcul automatique</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.nbrJoursOuvres">
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="supprimerMois(item)"
                    icon
                    small
                    color="red"
                    v-on="on"
                    :disabled="loading">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Supprimer</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>

        <!-- Modal d'édition -->
        <v-dialog v-model="editDialog" max-width="500px" persistent>
          <v-card>
            <v-toolbar color="#01579B" dark flat>
              <v-toolbar-title>
                {{ editedItem.mois ? `Modifier ${editedItem.mois} ${selectedYear}` : 'Nouveau mois' }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn @click="closeEditModal" dark fab small>
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="selectedYear"
                      label="Année"
                      readonly
                      outlined
                      dense>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editedItem.mois"
                      label="Mois"
                      readonly
                      outlined
                      dense>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editedItem.nbrJoursOuvres"
                      label="Nombre de jours ouvrés"
                      type="number"
                      min="15"
                      max="23"
                      outlined
                      dense
                      :error-messages="validationErrors"
                      hint="Entre 15 et 23 jours"
                      persistent-hint>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-alert
                  type="info"
                  outlined
                  class="mt-3">
                  <strong>Règles de calcul :</strong><br>
                  • Calcul basé sur les jours ouvrés (weekends + fériés exclus)<br>
                  • Restauration d'entreprise - Fermée weekends<br>
                  • Timezone Dakar (GMT+0)
                </v-alert>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                v-if="editedItem.nbrJoursOuvres"
                @click="supprimerMoisFromModal"
                color="red"
                text
                :disabled="loading">
                <v-icon left>mdi-delete</v-icon>
                Supprimer
              </v-btn>
              <v-btn
                @click="closeEditModal"
                text>
                Annuler
              </v-btn>
              <v-btn
                @click="sauvegarderMois"
                color="#01579B"
                class="white--text"
                :disabled="!isFormValid || loading">
                <v-icon left>mdi-content-save</v-icon>
                Sauvegarder
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Dialog de confirmation -->
        <v-dialog v-model="confirmDialog" max-width="400px">
          <v-card>
            <v-card-title class="headline">Confirmation</v-card-title>
            <v-card-text>{{ confirmMessage }}</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="confirmDialog = false" text>Annuler</v-btn>
              <v-btn @click="executeConfirmAction" color="primary" text>Confirmer</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import apiClient from "../../../config/api.js";

export default {
  name: "JoursOuvresAdmin",
  data() {
    return {
      loading: false,
      type: null,
      text: "",
      selectedYear: new Date().getFullYear(),
      availableYears: [],
      monthsData: [],
      editDialog: false,
      confirmDialog: false,
      confirmMessage: "",
      confirmAction: null,
      editedItem: {
        numeroMois: null,
        mois: "",
        nbrJoursOuvres: null
      },
      validationErrors: [],
      headers: [
        { text: "Mois", value: "mois", align: "center", sortable: false },
        { text: "Jours ouvrés", value: "nbrJoursOuvres", align: "center", sortable: false },
        { text: "Statut", value: "statut", align: "center", sortable: false },
        { text: "Actions", value: "actions", align: "center", sortable: false }
      ],
      monthNames: [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
      ]
    }
  },
  computed: {
    isFormValid() {
      const nbr = parseInt(this.editedItem.nbrJoursOuvres);
      return nbr >= 15 && nbr <= 23;
    }
  },
  created() {
    this.initializeYears();
    this.loadYearData();
  },
  methods: {
    initializeYears() {
      const currentYear = new Date().getFullYear();
      for (let year = currentYear - 2; year <= currentYear + 3; year++) {
        this.availableYears.push(year);
      }
    },
    
    initializeMonthsData() {
      this.monthsData = this.monthNames.map((monthName, index) => ({
        numeroMois: index + 1,
        mois: monthName,
        nbrJoursOuvres: null
      }));
    },

    async loadYearData() {
      if (!this.selectedYear) return;
      
      this.loading = true;
      this.initializeMonthsData();
      
      try {
        // Charger tous les jours ouvrés et filtrer par année côté front
        const response = await apiClient.get(
          `${this.$store.getters.user.baseUrl}/wallet/admin/jours-ouvres`
        );
        
        if (response.data.status === 200 && response.data.data) {
          // Filtrer les données par année sélectionnée
          const yearData = response.data.data.filter(item => item.annee === this.selectedYear);
          
          yearData.forEach(item => {
            const monthIndex = item.mois - 1;
            if (monthIndex >= 0 && monthIndex < 12) {
              this.monthsData[monthIndex].nbrJoursOuvres = item.nbrJoursOuvres;
            }
          });
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        this.showAlert("Erreur lors du chargement des données", "warning");
      } finally {
        this.loading = false;
      }
    },

    async calculerAnneeComplete() {
      this.showAlert("Fonctionnalité de calcul automatique non disponible - Les jours ouvrés sont calculés automatiquement par le système", "info");
    },

    async calculerMois(item) {
      this.showAlert(`Le calcul automatique pour ${item.mois} est géré par le système - Utilisez 'Modifier' pour forcer une valeur spécifique`, "info");
    },

    openEditModal(item) {
      this.editedItem = {
        numeroMois: item.numeroMois,
        mois: item.mois,
        nbrJoursOuvres: item.nbrJoursOuvres || ""
      };
      this.validationErrors = [];
      this.editDialog = true;
    },

    closeEditModal() {
      this.editDialog = false;
      this.editedItem = {
        numeroMois: null,
        mois: "",
        nbrJoursOuvres: null
      };
      this.validationErrors = [];
    },

    async sauvegarderMois() {
      if (!this.validateForm()) return;
      
      this.loading = true;
      try {
        const nbrJoursOuvres = parseInt(this.editedItem.nbrJoursOuvres);
        
        // Vérifier si le mois existe déjà
        const existsResponse = await apiClient.get(
          `${this.$store.getters.user.baseUrl}/wallet/admin/jours-ouvres/${this.selectedYear}/${this.editedItem.numeroMois}`
        );
        
        let response;
        if (existsResponse.data.status === 200) {
          // Mois existe - utiliser PUT
          response = await apiClient.put(
            `${this.$store.getters.user.baseUrl}/wallet/admin/jours-ouvres/${this.selectedYear}/${this.editedItem.numeroMois}?nbrJoursOuvres=${nbrJoursOuvres}`
          );
        } else {
          // Mois n'existe pas - utiliser POST
          const data = {
            annee: this.selectedYear,
            mois: this.editedItem.numeroMois,
            nbrJoursOuvres: nbrJoursOuvres
          };
          response = await apiClient.post(
            `${this.$store.getters.user.baseUrl}/wallet/admin/jours-ouvres`,
            data
          );
        }
        
        if (response.data.status === 200) {
          this.showAlert("Jours ouvrés sauvegardés avec succès", "info");
          await this.loadYearData();
          this.closeEditModal();
        } else {
          this.showAlert(response.data.message || "Erreur lors de la sauvegarde", "warning");
        }
      } catch (error) {
        console.error("Erreur lors de la sauvegarde:", error);
        this.showAlert("Erreur lors de la sauvegarde", "warning");
      } finally {
        this.loading = false;
      }
    },

    async supprimerMois(item) {
      this.confirmMessage = `Êtes-vous sûr de vouloir supprimer les jours ouvrés pour ${item.mois} ${this.selectedYear} ?`;
      this.confirmAction = async () => {
        await this.executeSuppression(item);
      };
      this.confirmDialog = true;
    },

    async supprimerMoisFromModal() {
      this.confirmMessage = `Êtes-vous sûr de vouloir supprimer les jours ouvrés pour ${this.editedItem.mois} ${this.selectedYear} ?`;
      this.confirmAction = async () => {
        await this.executeSuppression(this.editedItem);
        this.closeEditModal();
      };
      this.confirmDialog = true;
    },

    async executeSuppression(item) {
      this.loading = true;
      try {
        const response = await apiClient.delete(
          `${this.$store.getters.user.baseUrl}/wallet/admin/jours-ouvres/${this.selectedYear}/${item.numeroMois}`
        );
        
        if (response.data.status === 200) {
          this.showAlert("Suppression effectuée avec succès", "info");
          await this.loadYearData();
        } else {
          this.showAlert(response.data.message || "Erreur lors de la suppression", "warning");
        }
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        this.showAlert("Erreur lors de la suppression", "warning");
      } finally {
        this.loading = false;
        this.confirmDialog = false;
      }
    },

    executeConfirmAction() {
      if (this.confirmAction) {
        this.confirmAction();
      }
    },

    validateForm() {
      this.validationErrors = [];
      const nbr = parseInt(this.editedItem.nbrJoursOuvres);
      
      if (!nbr || nbr < 15 || nbr > 23) {
        this.validationErrors.push("Le nombre de jours ouvrés doit être entre 15 et 23");
        return false;
      }
      
      return true;
    },

    refresh() {
      this.loadYearData();
    },

    onCancel() {
      this.loading = false;
    },

    isCurrentMonth(monthNumber) {
      const now = new Date();
      return now.getFullYear() === this.selectedYear && now.getMonth() + 1 === monthNumber;
    },

    getStatusColor(nbrJours) {
      if (!nbrJours) return "orange"; // Non défini
      if (nbrJours >= 20 && nbrJours <= 23) return "green"; // Valeurs normales
      return "orange"; // Valeurs inhabituelles (15-19)
    },

    getStatusTextColor(nbrJours) {
      return "white";
    },

    getStatusIcon(nbrJours) {
      return nbrJours ? "mdi-check-circle" : "mdi-alert-circle";
    },

    getStatusIconColor(nbrJours) {
      return nbrJours ? "green" : "orange";
    },

    AlertColor(type) {
      return type === "info" ? "#4CAF50" : "#F44336";
    },

    showAlert(message, type) {
      this.text = message;
      this.type = type;

      let timer = this.showAlert.timer;
      if (timer) {
        clearTimeout(timer);
      }
      this.showAlert.timer = setTimeout(() => {
        this.type = null;
      }, 5000);
    },

    dismissible_close() {
      clearTimeout(this.showAlert.timer);
      this.type = null;
    }
  }
};
</script>

<style scoped>
.toolbar .v-toolbar__content {
  padding: 0 !important;
}

.custom-btn {
  width: 100%;
}

.custom-btn::before {
  color: transparent;
}

.custom-btn:hover {
  color: #F5F5F5 !important;
}

.v-data-table >>> th {
  background-color: #F5F5F5 !important;
  font-weight: bold !important;
}

.pre-formatted {
  white-space: pre-line;
}

.multi-line {
  word-break: break-word;
}
</style>